import React, { useCallback, useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Typography, Image } from 'lib/components';
import HospitalRow from './HospitalRow';
import { Checkbox } from 'lib/components/FormFields';
import { useWindowResize } from 'hooks/useWindowResize';
import { Hospital } from 'types';
import { HeaderItem, Sort, SortField } from './types';
import { hospitalEmailCompareFn, hospitalNameCompareFn } from './sort';
import classes from './index.module.scss';

interface Props {
	hospitals: Hospital[];
	checkedId: number;
	checkAction: (id: number, isChecked: boolean) => void;
}

const HEADER_ITEMS: HeaderItem[] = [
	{ label: 'Name', field: 'name', sort: 'asc' },
	{ label: 'Email', field: 'email', sort: 'asc' },
	{ label: 'Phone' },
	{ label: 'Employees' },
	{ label: 'Registered' },
];

const HospitalsDataTable = ({ hospitals, checkedId, checkAction }: Props) => {
	const [allChecked, setAllChecked] = useState(false);
	const { isSmallerDevice } = useWindowResize();
	const [sortFields] = useState(
		HEADER_ITEMS.filter((item) => item.field && item.sort).reduce<Record<string, Sort>>((acc, cur) => {
			if (cur.field && cur.sort) {
				acc[cur.field] = cur!.sort;
			}
			return acc;
		}, {})
	);
	const [currentSortField, setCurrentSortField] = useState<SortField>({
		sort: 'asc',
		field: 'name',
	});
	const [sortedHospitals, setSortedHospitals] = useState<Hospital[]>(hospitals);

	const sortNurses = useCallback(
		(hospitals: Hospital[]) => {
			const sorted = [...hospitals].sort((a, b) => {
				switch (currentSortField?.sort) {
					case 'asc':
						if (currentSortField?.field === 'name') {
							return hospitalNameCompareFn(a, b, 'asc');
						} else if (currentSortField?.field === 'email') {
							return hospitalEmailCompareFn(a, b, 'asc');
						}
						return 0;

					case 'desc':
						if (currentSortField?.field === 'name') {
							return hospitalNameCompareFn(a, b, 'desc');
						} else if (currentSortField?.field === 'email') {
							return hospitalEmailCompareFn(a, b, 'desc');
						}
						return 0;

					default:
						return 0;
				}
			});
			return sorted;
		},
		[currentSortField]
	);

	useEffect(() => {
		setSortedHospitals(sortNurses(hospitals));
	}, [hospitals, sortNurses]);

	useEffect(() => {
		const sorted = sortNurses(sortedHospitals);

		setSortedHospitals(sorted);
	}, [sortNurses, setSortedHospitals]);

	const handleHeaderItemClick = useCallback(
		(headerItem: HeaderItem) => {
			if (headerItem.field && sortFields[headerItem.field]) {
				if (headerItem.field === currentSortField?.field) {
					setCurrentSortField((prev) => {
						return {
							...prev,
							sort: prev?.sort === 'asc' ? 'desc' : 'asc',
						};
					});
				} else if (currentSortField?.field !== headerItem.field) {
					setCurrentSortField({ field: headerItem.field, sort: 'desc' });
				}
			}
		},
		[currentSortField, sortFields, setCurrentSortField]
	);

	const renderHeaderItem = (headerItem: HeaderItem) => {
		if (!headerItem.field || !headerItem.sort) {
			return (
				<div key={Math.random()} className={classes['col']}>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					{/* <Image src={'icTableDown'} /> */}
				</div>
			);
		} else {
			return (
				<div
					onClick={() => handleHeaderItemClick(headerItem)}
					key={Math.random()}
					className={classes['col']}
					style={{
						cursor: 'pointer',
					}}
				>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					<Image
						src={'icTableDown'}
						style={{
							marginLeft: '0.5rem',
							transform:
								currentSortField?.field === headerItem.field && currentSortField.sort === 'desc'
									? 'rotate(180deg)'
									: 'initial',
						}}
					/>
				</div>
			);
		}
	};

	return (
		<div className={classes['inventories-datatable']}>
			<div className={classes['table-wrapper']}>
				<div className={classes['table-container']}>
					<div className={classes['toolbar']}>
						<Checkbox
							checked={allChecked}
							onChange={() => setAllChecked(!allChecked)}
							style={{
								margin: '0 2rem 0.1rem 2rem',
							}}
						/>
						{HEADER_ITEMS.map((item) => renderHeaderItem(item))}
					</div>
					<div className={classes['container']}>
						<Scrollbars
							style={{
								flex: 1,
							}}
						>
							{sortedHospitals.map((hospital) => (
								<HospitalRow
									key={hospital.id}
									hospital={hospital}
									checkedId={checkedId}
									checkAction={checkAction}
								/>
							))}
						</Scrollbars>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HospitalsDataTable;
